<template>
  <a v-if='page' :href="'/' + uid" class='event-row'>
    <div class='event-header'>
      <h3 class='caret' v-html='page.title' />
      <h3 class='event-date'>{{ shortDate }}</h3>
    </div>
    <div class='event-body'>
      <div class='event-details'>
        <div class='event-actions'>
          <span class='event-tag' v-if='tag'>{{ tag }}</span>
          <a class='event-ical' :href='icalUrl' @click.stop>› Add to Calendar</a>
        </div>
        <div class='event-middle'>
          <p class='event-meta' v-if='location'>{{ location }}</p>
          <p class='event-meta' v-if='fullDate'>{{ fullDate }}</p>
        </div>
        <p class='event-intro' v-if='intro'>{{ intro }}</p>
      </div>
      <figure class='event-image' v-if='imageUrl'>
        <img :src='imageUrl' :alt='page.title' />
      </figure>
    </div>
  </a>
</template>

<script setup>
  import { computed } from 'vue'
  import { useStore } from '../KQL.js'
  import { pageFromPath } from '../helpers.js'

  const kql = useStore();
  const props = defineProps(['uid', 'intro']);

  const page = computed(() => props.uid ? pageFromPath(props.uid, kql.pages) : null);

  const tag = computed(() => {
    if (page.value?.pagetype === 'exhibition') return 'Exhibition';
    if (page.value?.pagetype === 'event') return 'Event';
    return '';
  });

  const location = computed(() => page.value?.location || page.value?.exhibitionlocation || '');

  const imageUrl = computed(() =>
    page.value?.eventposter?.[0]?.url ||
    page.value?.exhibitionposter?.[0]?.url ||
    page.value?.coverimages?.[0]?.url ||
    null
  );

  const icalUrl = computed(() => {
    const dateStr = page.value?.eventdate || page.value?.startdate || '';
    return dateStr ? `/events/${props.uid}.ics` : '#';
  });

  const fmtDate = d => { const [y, m, day] = d.split('-'); return `${m}/${day}/${y}`; };

  const shortDate = computed(() => {
    const p = page.value;
    if (!p) return '';
    const isExhibition = p.pagetype === 'exhibition';
    const dateStr = p.eventdate || p.startdate || '';
    const endStr = isExhibition ? (p.enddate || '') : '';
    if (!dateStr) return p.appendix || '';
    return endStr ? `${fmtDate(dateStr)}–${fmtDate(endStr)}` : fmtDate(dateStr);
  });

  const fullDate = computed(() => {
    const p = page.value;
    if (!p) return '';
    const dateStr = p.eventdate || p.startdate || '';
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T12:00:00');
    const dayDate = date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    const timeStr = p.eventtime || '';
    if (!timeStr) return dayDate;
    const [h, m] = timeStr.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'pm' : 'am';
    const hour12 = hour % 12 || 12;
    const mins = m !== '00' ? `:${m}` : '';
    return `${dayDate} from ${hour12}${mins}${ampm}`;
  });
</script>

<style scoped>
  .event-row {
    line-height: 1.2em;
    box-sizing: border-box;
    border-top: 1px solid var(--border);
    margin: 0;
    display: flex;
    flex-direction: column;
    padding-top: 1rem;
    padding-bottom: 4rem;
    color: inherit;
    text-decoration: none;
  }

  h3 {
    padding-left: 0.75em;
    line-height: 1.1em;
    margin: 0;
    position: relative;
    font-size: 2.5rem;
  }

  h3.caret::before {
    position: absolute;
    left: 0;
    content: '\203a';
    transition: transform 0.1s ease-in-out;
  }

  h3:not(.caret) { text-align: right; }

  h3.caret { transition: opacity 0.2s ease; }

  .event-row:hover h3.caret { opacity: 0.65; }
  .event-row:hover h3.caret::before { transform: translateX(0.1em); }

  .event-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 1rem;
    margin-bottom: 0.5rem;
  }

  .event-date { white-space: nowrap; flex-shrink: 0; padding-left: 0; }

  .event-body {
    display: flex;
    gap: 2rem;
    align-items: stretch;
    padding-left: 2.125rem;
  }

  .event-details { flex: 1; display: flex; flex-direction: column; }

  .event-middle {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    margin-top: 0.25rem;
  }

  .event-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.4rem;
    margin-bottom: 0.6rem;
  }

  .event-tag {
    font-size: 0.75rem;
    font-family: "Maison Neue Demi", sans-serif;
    background: rgba(255,255,255,0.15);
    border-radius: 2em;
    padding: 0.15em 0.8em;
    letter-spacing: 0.02em;
    vertical-align: 0.1em;
  }

  .event-ical {
    font-size: 0.85rem;
    font-family: "Maison Neue Demi", sans-serif;
    letter-spacing: 0.01em;
    color: inherit;
    text-decoration: none;
    opacity: 0.6;
  }

  .event-ical:hover { opacity: 1; }

  .event-meta {
    margin: 0;
    font-size: 0.85rem;
    font-family: "Maison Neue Demi", sans-serif;
    opacity: 0.7;
    line-height: 1.4em;
    letter-spacing: 0.01em;
  }

  .event-intro {
    padding-top: 1.5rem;
    font-size: 1.1rem;
    line-height: 1.45em;
    max-width: 36em;
  }

  .event-image {
    flex: 0 0 auto;
    width: 28%;
    margin: 0;
    padding: 0;
    padding-top: 1.75rem;
  }

  .event-image img {
    width: 100%;
    display: block;
    max-height: 30rem;
    object-fit: cover;
    object-position: top;
  }

  @media (max-width: 600px) {
    .event-body { flex-direction: column; }
    .event-image { width: 100%; }
    h3 { font-size: 2.25rem; }
  }
</style>
